<template>
  <div class="train-details">
    <div class="row align-items-center">
      <!-- Train Details Column -->
      <div class="col-8">
        <div v-if="outboundTrain" class="mb-1">
          <p class="mb-0 caption">
            <span class="caption-strong">{{ t('reservations.outbound') }}</span> {{ formatDate(outboundTrain.departureDate) }} {{ outboundTrain.departureStation }} {{ outboundTrain.departureTime }} → {{ outboundTrain.arrivalStation }} {{ outboundTrain.arrivalTime }}
          </p>
        </div>
        <div v-if="inboundTrain" class="mb-1">
          <p class="mb-0 caption">
            <span class="caption-strong">{{ t('reservations.return') }}</span> {{ formatDate(inboundTrain.departureDate) }} {{ inboundTrain.departureStation }} {{ inboundTrain.departureTime }} → {{ inboundTrain.arrivalStation }} {{ inboundTrain.arrivalTime }}
          </p>
        </div>
        <p class="mb-0 caption">{{ getTrainClass() }} {{ t('reservations.class') }}</p>
      </div>
      
      <!-- Train Operator Logo Column -->
      <div class="col-4 d-flex align-items-start justify-content-center">
        <img 
          v-if="getPrimaryTrainOperator()" 
          :src="getTrainOperatorIconUrl(getPrimaryTrainOperator())"
          :alt="`${getPrimaryTrainOperator()} logo`"
          class="train-operator-logo"
          @error="handleImageError"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
      import { defineProps, computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { getTrainStationName, getTrainCompanyName } from '@/utils/locationCodes'

  // Initialize i18n
  const { t } = useI18n()
  
  

  

  


  // Define the props
  const props = defineProps<{
    tripInformation: any
  }>()

  // Get train details object
  const trainDetail = computed(() => {
    if (Array.isArray(props.tripInformation) && props.tripInformation.length > 0) {
      // Check for the correct structure: tripInformation[0].details
      return props.tripInformation[0].details || props.tripInformation[0].tripInformation
    }
    return props.tripInformation
  })

  // Get outbound train information
  const outboundTrain = computed(() => {
    const train = trainDetail.value
    if (train?.outboundTrip) {
      const departureStation = getTrainStationName(train.outboundTrip.departure?.location || '')
      const arrivalStation = getTrainStationName(train.outboundTrip.arrival?.location || '')
      const departureDate = train.outboundTrip.departure?.date || ''
      const departureTime = train.outboundTrip.departure?.time || ''
      const arrivalDate = train.outboundTrip.arrival?.date || ''
      const arrivalTime = train.outboundTrip.arrival?.time || ''
      const operatorCode = getTrainCompanyName(train.outboundTrip.company || '')
      return {
        departureStation,
        arrivalStation,
        departureDate,
        departureTime,
        arrivalDate,
        arrivalTime,
        operatorCode
      }
    }
    return null
  })

  // Get inbound train information
  const inboundTrain = computed(() => {
    const train = trainDetail.value
    if (train?.inboundTrip) {
      const departureStation = getTrainStationName(train.inboundTrip.departure?.location || '')
      const arrivalStation = getTrainStationName(train.inboundTrip.arrival?.location || '')
      const departureDate = train.inboundTrip.departure?.date || ''
      const departureTime = train.inboundTrip.departure?.time || ''
      const arrivalDate = train.inboundTrip.arrival?.date || ''
      const arrivalTime = train.inboundTrip.arrival?.time || ''
      const operatorCode = getTrainCompanyName(train.inboundTrip.company || '')
      return {
        departureStation,
        arrivalStation,
        departureDate,
        departureTime,
        arrivalDate,
        arrivalTime,
        operatorCode
      }
    }
    return null
  })

  // Handle image loading errors
  const handleImageError = (event: Event) => {
    const img = event.target as HTMLImageElement
    img.style.display = 'none' // Hide the image if it fails to load
  }

  // Helper function to get the primary train operator code (prefer outbound, fallback to inbound)
  const getPrimaryTrainOperator = (): string => {
    const outboundOperator = outboundTrain.value?.operatorCode
    const inboundOperator = inboundTrain.value?.operatorCode
    return outboundOperator || inboundOperator || ''
  }

  // Train company image mapping
  const trainImages: Record<string, string> = {
    'Eurostar': require('@/assets/carrier-logos/Eurostar.png'),
    '9F': require('@/assets/carrier-logos/Eurostar.png'),
    // Add more train company logos as needed
  }

  // Helper function to get train operator icon URL
  const getTrainOperatorIconUrl = (operatorCode: string): string => {
    return trainImages[operatorCode] || ''
  }

  // Helper function to format date as DD/MM/YY
  const formatDate = (dateString: string): string => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear().toString().slice(-2)
    return `${day}/${month}/${year}`
  }

  // Map train class names to display names
  const formatTrainClass = (trainClass: string): string => {
    const classMap: Record<string, string> = {
      'Premium': 'Première',
      'Standart': 'Standard',
      'Standard': 'Standard',
      'Economy': 'Standard',
      'Eco': 'Standard',
      'First': 'Première',
      'Business': 'Première'
    }
    return classMap[trainClass] || trainClass
  }

  // Helper function to get train class
  const getTrainClass = () => {
    const train = trainDetail.value
    
    let rawClass = ''
    if (train?.class) {
      rawClass = train.class
    } else if (train?.outboundTrip?.class) {
      rawClass = train.outboundTrip.class
    } else if (train?.inboundTrip?.class) {
      rawClass = train.inboundTrip.class
    } else {
      rawClass = 'Standard'
    }
    
    return formatTrainClass(rawClass)
  }
</script>

<style scoped>
  .train-details {
    padding: 0.25rem 0;
  }

  .train-operator-logo {
    width: 120px;
    height: 40px;
    object-fit: contain;
    border-radius: 4px;
  }
</style> 